import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { uniq } from 'lodash';
import {
  AdminContext,
  AuthContext,
  AuthContextUser,
  ExternalAuthContext,
  LayoutContext,
  SetupBrokerageWizardModalContext,
} from 'contexts';
import { UserRoles } from '@bb/users';
import { useBreakpoint } from 'hooks';
import {
  buildPusherChannel,
  PusherChannel,
  PusherChannelTypes,
  HEARTBEAT_PREFIX,
  ONGOING_TRANSFER_PREFIX,
  COUNT_SOCKET_PREFIX,
  featureEnabled,
} from 'utils';
import errorReporter from '@app/errorReporter';
import styled from 'styled-components';
import { getCountUpdate } from 'api/AppointmentController';
import { Feature } from '@bb/automaton';
import { BadgeItemType } from './types';
import Text from '../../Typography/Text';
import Icon, { IconNameType } from '../../Icon';
import Badge, { BadgeProps } from '../../Badge';
import Popconfirm from '../../Popconfirm';

import BrokerageMenu from './BrokerageMenu';
import AnsweringMenu from './AnsweringMenu';
import DashboardMenu from './DashboardMenu';
import RetsMenu from './RetsMenu';
import { MenuItemContainer, StyledAdminQueueItem } from '../StyledComponents';
import MLSStaffMenu from './MLSStaffMenu';

const brokerageLinks = [
  '/admin/dashboard',
  '/listing/list/brokerage',
  '/my_business',
  '/my_business/showing',
  '/listing/list/team/',
  '/listing/list/favorites',
  '/admin',
  '/listing/network_map',
  '/analytics/market',
  '/analytics/dashboard',
  '/analytics/insight',
  '/contacts/client',
  '/lockboxes',
  '/lockboxesActivity',
];

const answeringLinks = [
  '/answering/queue',
  '/answering/brokerages',
  '/answering/settings',
];

const newAnsweringLinks = [
  '/answering/queue',
  '/answering/search',
  '/answering/settings',
];

const mlsStaffLinks = ['/mls/users', '/mls/lockboxes', '/mls/brokerages'];
if (featureEnabled('quickLockboxAssignmentFeature')) {
  mlsStaffLinks.push('/mls/quick-assignment');
}
if (featureEnabled('supraBillingApiFeature'))
  mlsStaffLinks.push('/mls/contracts');
mlsStaffLinks.push('/answering/settings');

const dashboardLinks = [
  '/listing',
  '/appointments',
  '/offers',
  '/updates',
  '/notifications',
  '/my_agent',
];

const retsLinks = [
  '/listing',
  '/appointments/book',
  '/appointments/offer',
  '/appointments/updates',
];

// Conditionally adds a popover if actions should be prevented.
const MenuSectionWrapper = ({
  isAdminAccessRestricted,
  to,
  openBrokerageSetupWizardModal,
  auth,
  children,
}: {
  isAdminAccessRestricted: boolean;
  to: string;
  openBrokerageSetupWizardModal: () => void;
  auth: AuthContextUser;
  children: React.ReactNode;
}) => {
  if (!isAdminAccessRestricted) return <Link to={to}>{children}</Link>;

  // the popconfirm would be different based on the user role
  return auth.roles[auth.role] <= auth.roles[UserRoles['Branch Admin']] ? (
    <Popconfirm
      title={
        <StyledModalTextContainer>
          <Text strong className="modalTitle">
            Brokerage hasn&apos;t been set up
          </Text>
          <div className="modalText">
            To access administrative features, a Global Admin, Manager, or Owner
            must finish setting up your brokerage.
          </div>
        </StyledModalTextContainer>
      }
      okText="OK"
      maxWidth={370}
      placement="right"
      trigger="click"
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      {children}
    </Popconfirm>
  ) : (
    <Popconfirm
      title={
        'To access administrative features you must finish setting up your brokerage'
      }
      okText="Continue Setup"
      cancelText="Cancel"
      maxWidth={300}
      placement="right"
      onConfirm={openBrokerageSetupWizardModal}
      okButtonProps={{
        disabled:
          // Can only be setup by Global Admins or higher
          auth.roles[auth.role] <= auth.roles[UserRoles['Branch Admin']],
      }}
      trigger="click"
    >
      {children}
    </Popconfirm>
  );
};

const MenuSection: React.FC = () => {
  const { siderCollapsed } = React.useContext(LayoutContext);
  const { auth, pusher } = React.useContext(AuthContext);
  const features = auth?.org.features;

  // MLS Staff Feature Map should be created in the same order as the menu items from MLSStaffMenu.
  // This is used to set the menu index when the user makes a selection.
  let mlsStaffFeatureMap: { [key in string]: string } | undefined;
  if (features) {
    mlsStaffFeatureMap = {
      [Feature.MLS_STAFF_MANAGE_USERS]: '/mls/users',
      [Feature.MLS_STAFF_MANAGE_LOCKBOXES]: '/mls/lockboxes',
      [Feature.MLS_STAFF_MANAGE_BROKERAGES]: '/mls/brokerages',
    };
    if (
      features.includes('MLS_STAFF_QUICK_LOCKBOX_ASSIGNMENT') &&
      featureEnabled('quickLockboxAssignmentFeature')
    ) {
      mlsStaffFeatureMap[Feature.MLS_STAFF_QUICK_LOCKBOX_ASSIGNMENT] =
        '/mls/quick-assignment';
    }
    if (
      features.includes('MLS_STAFF_VIEW_CONTRACTS') &&
      featureEnabled('supraBillingApiFeature')
    ) {
      mlsStaffFeatureMap[Feature.MLS_STAFF_VIEW_CONTRACTS] = '/mls/contracts';
    }
    mlsStaffFeatureMap[Feature.ADMIN] = '/answering/settings';
  }

  // SetupBrokerageWizardModalContext is not available in external router.
  const { openModal: openBrokerageSetupWizardModal } =
    React.useContext(SetupBrokerageWizardModalContext) || {};
  const {
    queueCount,
    ongoingTransferCount,
    shouldDisplayAdminQueue,
    shouldDisplayAdminTools,
    shouldDisplayShowings,
    fetchQueueCount,
    fetchOngoingTransferCount,
    isAdminAccessRestricted,
  } = React.useContext(AdminContext) || {};
  const { auth: extAuth } = React.useContext(ExternalAuthContext);

  const [channels, setChannels] = React.useState<
    Partial<
      Record<
        Extract<PusherChannelTypes, 'aq' | 'ongoingTransfer' | 'cs'>,
        PusherChannel
      >
    >
  >({ aq: null, ongoingTransfer: null, cs: null });

  const [coopShowingsActionCount, setCoopShowingsActionCount] =
    React.useState(0);
  const [listingShowingsActionCount, setListingShowingsActionCount] =
    React.useState(0);
  const showingsCount = React.useMemo(
    () => coopShowingsActionCount + listingShowingsActionCount,
    [coopShowingsActionCount, listingShowingsActionCount],
  );
  const fetchShowingsCount = React.useCallback(async () => {
    if (!auth) return;

    const [
      { coopAppointmentActionCount },
      { listingAgentAppointmentActionCount },
    ] = await Promise.all([
      getCountUpdate('coopAppointmentActionCount'),
      getCountUpdate('listingAgentAppointmentActionCount'),
    ]);
    setCoopShowingsActionCount(coopAppointmentActionCount);
    setListingShowingsActionCount(listingAgentAppointmentActionCount);
  }, [auth]);

  React.useEffect(() => {
    fetchShowingsCount();
  }, [fetchShowingsCount]);

  const [selectedKey, setSelectedKey] = React.useState('');
  const [openKeys, setOpenKeys] = React.useState<string[]>([]);
  const location = useLocation();
  const { md } = useBreakpoint();
  const disableToolTip = !md && siderCollapsed;

  const useEnhancedAnswering = React.useMemo(
    () => auth?.org.type === 'answering' || auth?.org.type === 'mlsStaff',
    [auth],
  );

  const getMLSStaffLinks = () => {
    if (!mlsStaffFeatureMap) return [];
    const orgFeatures: string[] = auth?.org.features || [];
    return Object.entries(mlsStaffFeatureMap).reduce(
      (links, [feature, link]) => {
        if (orgFeatures.includes(feature)) {
          links.push(link);
        }
        return links;
      },
      [] as string[],
    );
  };

  const getAnweringLinks = (enhancedAnswering: boolean) => {
    const orgFeatures: string[] = auth?.org.features || [];
    const includeAdmin =
      !featureEnabled('orgAdminFeature') || orgFeatures.includes('ADMIN');
    const linksToFilter = enhancedAnswering
      ? newAnsweringLinks
      : answeringLinks;
    return linksToFilter.filter(
      link => link !== '/answering/settings' || includeAdmin,
    );
  };

  const getBrokerageLinks = () => {
    const orgFeatures: string[] = auth?.org.features || [];
    const includeAdmin =
      !featureEnabled('orgAdminFeature') || orgFeatures.includes('ADMIN');
    return brokerageLinks.filter(link => link !== '/admin' || includeAdmin);
  };

  const getKeyUsingLocation = () => {
    const { pathname } = location;

    let links;

    const displayedBrokerageLinks = getBrokerageLinks();
    if (extAuth) {
      links = extAuth.retsDashboard ? retsLinks : dashboardLinks;
    } else {
      links =
        auth?.org.type === 'brokerage'
          ? displayedBrokerageLinks
          : getAnweringLinks(useEnhancedAnswering);

      if (auth?.org.type === 'mlsStaff') {
        links = getMLSStaffLinks();
      }
    }

    // Use index of the most specific link matched
    let key = links.reduce<[number | string, string]>(
      (acc, link, index) => {
        if (pathname.indexOf(link) !== -1 && link.length > acc[1].length) {
          return [index, link];
        }
        return acc;
      },
      [-1, ''],
    )[0];
    // We also need to map '/my_business/showings/buyer' to '/my_business/showings/listing',
    // This is just a temporary workaround until agent calendar is combined into one page/url
    if (
      links === displayedBrokerageLinks &&
      pathname.startsWith('/my_business/showings/listing')
    ) {
      key = displayedBrokerageLinks.indexOf('/my_business/showings/buyer');
    }

    // Map admin sub-pages to the Admin menu item so it stays
    // highlighted when navigating to those sub-pages.
    const adminSubPagePaths = [
      '/listing/list/deleted',
      '/listing/new',
      '/listing/merge',
      '/announcement',
      '/template',
      '/org/settings',
      '/billing',
      '/users',
    ];

    if (
      links === displayedBrokerageLinks &&
      adminSubPagePaths.some(path => pathname.startsWith(path))
    ) {
      setSelectedKey(displayedBrokerageLinks.indexOf('/admin').toString());
      return;
    }

    const isShowingsSubMenuForListing = pathname.match(
      /\/appointments\/listing\/.+/,
    );
    const isListingsSubMenu =
      !isShowingsSubMenuForListing && pathname.match(/\/listing\/(?!.*list).+/);
    const isShowingsSubMenuForBuyer = pathname.match(
      'buyer_contact_appointments',
    );

    // To handle new My Listings and Showings sub-menu items on the Client Dashboard
    if (isListingsSubMenu) {
      setOpenKeys(uniq([...openKeys, 'myListings']));
      key = `myListings-${pathname.split('/listing/')[1]}`;
    }
    if (isShowingsSubMenuForListing) {
      setOpenKeys(uniq([...openKeys, 'showings']));
      key = `showings-${pathname.split('/listing/')[1]}`;
    }
    if (isShowingsSubMenuForBuyer) {
      setOpenKeys(uniq([...openKeys, 'showings']));
      key = `showings-buyer`;
    }

    setSelectedKey(key.toString());
  };

  const toggleSockets = React.useCallback(
    (method: 'bind' | 'unbind') => {
      const heartbeatChannel = channels.aq;
      const ongoingTransferChannel = channels.ongoingTransfer;
      const countSocketChannel = channels.cs;

      if (heartbeatChannel) {
        heartbeatChannel[method]('appointment_status', fetchQueueCount);
        heartbeatChannel[method]('answering_settings_toggled', fetchQueueCount);
      }

      if (ongoingTransferChannel) {
        ongoingTransferChannel[method](
          'ongoing_transfer',
          fetchOngoingTransferCount,
        );
      }

      if (countSocketChannel) {
        countSocketChannel[method](
          'coopAppointmentActionCount',
          ({
            coopAppointmentActionCount,
          }: {
            coopAppointmentActionCount: number;
          }) => {
            setCoopShowingsActionCount(coopAppointmentActionCount);
          },
        );
        countSocketChannel[method](
          'listingAgentAppointmentActionCount',
          ({
            listingAgentAppointmentActionCount,
          }: {
            listingAgentAppointmentActionCount: number;
          }) => {
            setListingShowingsActionCount(listingAgentAppointmentActionCount);
          },
        );
        countSocketChannel[method](
          'allCounts',
          ({
            coopAppointmentActionCount,
            listingAgentAppointmentActionCount,
          }: {
            coopAppointmentActionCount: number;
            listingAgentAppointmentActionCount: number;
          }) => {
            setCoopShowingsActionCount(coopAppointmentActionCount);
            setListingShowingsActionCount(listingAgentAppointmentActionCount);
          },
        );
      }
    },
    [channels, fetchQueueCount, fetchOngoingTransferCount],
  );

  const badgeItemPropMap: Record<
    BadgeItemType,
    {
      key: string;
      label: string;
      count: number;
      icon: IconNameType;
      badgeOffset: BadgeProps['offset'];
      shouldDisplay: boolean;
      shouldRestrictAdminAccess: boolean;
    }
  > = {
    queue: {
      key: '0',
      label: 'Admin Queue',
      count: queueCount,
      icon: 'inbox',
      badgeOffset: [11, 11],
      shouldDisplay: shouldDisplayAdminQueue,
      shouldRestrictAdminAccess: true,
    },
    showings: {
      key: '3',
      label: 'Showings',
      count: showingsCount,
      icon: 'calendar',
      badgeOffset: [4, 11],
      shouldDisplay: shouldDisplayShowings,
      shouldRestrictAdminAccess: false,
    },
    tools: {
      key: '6',
      label: 'Admin',
      count: ongoingTransferCount,
      icon: 'tool',
      badgeOffset: [7, 11],
      shouldDisplay: shouldDisplayAdminTools,
      shouldRestrictAdminAccess: true,
    },
  };

  const renderBadgeItem = (itemType: BadgeItemType, to: string) => {
    const {
      key,
      label,
      count,
      icon,
      badgeOffset,
      shouldDisplay,
      shouldRestrictAdminAccess,
    } = badgeItemPropMap[itemType];
    return (
      shouldDisplay && (
        <StyledAdminQueueItem
          key={key}
          title={disableToolTip ? null : label}
          $siderCollapsed={siderCollapsed}
          icon={
            siderCollapsed ? (
              <Badge
                color="primary"
                count={count}
                overflowCount={99}
                offset={badgeOffset}
              >
                <Icon
                  type={icon}
                  color={selectedKey === key ? 'primary' : null}
                />
              </Badge>
            ) : (
              <Icon
                type={icon}
                color={selectedKey === key ? 'primary' : null}
              />
            )
          }
          data-cy={`side-menu-${itemType}`}
        >
          <MenuSectionWrapper
            isAdminAccessRestricted={
              shouldRestrictAdminAccess && isAdminAccessRestricted
            }
            to={to}
            auth={auth}
            openBrokerageSetupWizardModal={openBrokerageSetupWizardModal}
          >
            <MenuItemContainer>
              <span className="admin-queue-text">{label}</span>{' '}
              {!siderCollapsed && (
                <Badge
                  size="default"
                  count={count}
                  overflowCount={99}
                  color="primary"
                />
              )}
            </MenuItemContainer>
          </MenuSectionWrapper>
        </StyledAdminQueueItem>
      )
    );
  };

  const componentProps = () => {
    if (extAuth) {
      const selectedKeyPrefix = selectedKey.split('-')[0];
      return {
        selectedKey,
        openKeys,
        setOpenKeys,
        listings: extAuth.listings,
        disableToolTip,
        getIconColor: (key: string): 'primary' | null =>
          key === selectedKey ||
          (key === selectedKeyPrefix && !openKeys.includes(selectedKeyPrefix))
            ? 'primary'
            : null,
      };
    }

    return {
      selectedKey,
      disableToolTip,
      renderBadgeItem,
      getIconColor: (key: string | string[]): 'primary' | null =>
        (Array.isArray(key) ? key.includes(selectedKey) : key === selectedKey)
          ? 'primary'
          : null,
    };
  };

  React.useEffect(() => {
    if (channels.aq || channels.ongoingTransfer || channels.cs) {
      toggleSockets('bind');
      return () => toggleSockets('unbind');
    }
  }, [toggleSockets, channels]);

  React.useEffect(() => {
    getKeyUsingLocation();
  }, [location, auth, extAuth]);

  React.useEffect(() => {
    if (!auth || !pusher) {
      return;
    }
    const countSocketChannelName = buildPusherChannel(
      COUNT_SOCKET_PREFIX,
      auth.id,
    );

    const countSocketChannel = pusher.subscribe(countSocketChannelName);

    if (auth.roles[auth.role] < auth.roles.TEAM_ADMIN) {
      setChannels({ cs: countSocketChannel });
      return;
    }

    const userConnect = () => {
      axios
        .get('/appointment/heartbeat/subscribe')
        .then(({ status }) => {
          if (status === 403) {
            window.localStorage.setItem('loggedOut', 'true');
          } else {
            const heartbeatChannelName = buildPusherChannel(
              HEARTBEAT_PREFIX,
              auth.org.id,
            );
            const ongoingTransferChannelName = buildPusherChannel(
              ONGOING_TRANSFER_PREFIX,
              auth.org.id,
            );

            const heartbeatChannel = pusher.subscribe(heartbeatChannelName);
            const ongoingTransferChannel = pusher.subscribe(
              ongoingTransferChannelName,
            );
            setChannels({
              aq: heartbeatChannel,
              ongoingTransfer: ongoingTransferChannel,
              cs: countSocketChannel,
            });
          }
        })
        .catch(errorReporter.error);
    };

    userConnect();
    fetchQueueCount();
    fetchOngoingTransferCount();
  }, [auth]);

  if (!auth && !extAuth) return null;

  if (auth) {
    if (auth.org.type === 'brokerage') {
      return <BrokerageMenu {...componentProps()} />;
    }
    if (auth.org.type === 'mlsStaff') {
      return <MLSStaffMenu {...componentProps()} />;
    }
    return <AnsweringMenu {...componentProps()} />;
  }

  if (extAuth?.retsDashboard) {
    return <RetsMenu {...componentProps()} />;
  }
  return <DashboardMenu {...componentProps()} />;
};

export default MenuSection;

const StyledModalTextContainer = styled.div`
  margin: 0 1rem;
  display: flex;
  flex-direction: column;
  & .modalTitle {
    margin-bottom: 1rem;
  }
  & .modalText {
    margin-bottom: 1rem;
    margin-right: 1rem;
  }
`;
